import { useLocation, useNavigate } from "react-router";
import { cx } from "./ui";
import { Avatar } from "./bits";
import { useApprovals } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { approvalsForKid, setSelectedKid, useSelectedKid } from "@/lib/selectedKid";

/** Screens that follow the selected kid; anywhere else, picking a kid takes you to their page. */
const SCOPED = ["/parent", "/parent/approvals"];

/**
 * "All kids" plus one chip per kid, always on screen in the parent app: docked above the bottom
 * tabs on phones (within thumb reach) and listed in the sidebar on tablets and desktop.
 */
export function KidSwitcher({ variant }: { variant: "bar" | "sidebar" }) {
  const { kids } = useSession();
  const selected = useSelectedKid();
  const approvals = useApprovals();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (kids.length === 0) return null;

  function pick(kidId: string | null) {
    setSelectedKid(kidId);
    if (!SCOPED.includes(pathname.replace(/\/$/, ""))) navigate("/parent");
  }

  const options = [
    // The Approvals tab already counts the whole family, so "All kids" carries no badge.
    { id: null, name: "All kids", avatar: null, count: 0 },
    ...kids.map((k) => ({ id: k.id, name: k.name, avatar: k, count: approvals.data ? approvalsForKid(approvals.data, k.id).count : 0 })),
  ];

  return (
    <div
      role="group"
      aria-label="Show kid"
      className={cx(
        variant === "bar" ? "flex gap-1.5 overflow-x-auto px-2 py-2 [scrollbar-width:none]" : "flex flex-col gap-1",
      )}
    >
      {options.map((o) => {
        const active = (selected?.id ?? null) === o.id;
        return (
          <button
            key={o.id ?? "all"}
            type="button"
            aria-pressed={active}
            onClick={() => pick(o.id)}
            className={cx(
              "relative flex min-h-11 shrink-0 items-center gap-1.5 rounded-full py-1 pl-1 pr-3 text-sm font-extrabold transition",
              variant === "sidebar" && "w-full rounded-2xl pr-3",
              active ? "bg-brand-soft text-brand-strong ring-2 ring-brand" : "bg-sunken text-muted ring-1 ring-line hover:text-ink",
            )}
          >
            {o.avatar ? (
              <span aria-hidden>
                <Avatar member={o.avatar} size="sm" />
              </span>
            ) : (
              <span aria-hidden className="flex size-8 items-center justify-center rounded-full bg-card text-lg">
                👨‍👩‍👧
              </span>
            )}
            <span className={cx("truncate", variant === "sidebar" && "flex-1 text-left")}>
              {/* Short on phones so a few kids fit on one row; screen readers still hear "All kids". */}
              {o.id === null && variant === "bar" ? (
                <>
                  All<span className="sr-only"> kids</span>
                </>
              ) : (
                o.name
              )}
            </span>
            {o.count > 0 && (
              <span
                className={cx(
                  "min-w-5 rounded-full bg-danger px-1 text-center text-[11px] font-extrabold leading-5 text-white",
                  variant === "bar" && "absolute -top-1 left-6 min-w-4 text-[10px] leading-4",
                )}
              >
                {o.count > 99 ? "99+" : o.count}
                <span className="sr-only"> waiting</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
